import { useIsFreemiumUser } from 'hooks/use-freemium-user/use-freemium-user';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useAppUiActions } from 'store/app-ui/use-app-ui-actions';
import {
    selectOfflineReportsAccountRequestsLoading,
    selectOfflineReportsClosedRequests,
    selectOfflineReportsOpenRequests,
    selectOfflineReportsRequestsLoading,
    selectOfflineReportsSharedWithMeRequests,
    selectOfflineReportsSharedWithMeRequestsLoading,
    selectOfflineReportsUserAccountInfo,
} from 'store/offline-reports/selectors/offline-reports-selectors-factory';
import { useOfflineReportsActions } from 'store/offline-reports/use-offline-reports-actions';
import { OPEN_CATALOG_BUTTON_PATH } from '../consts';
import { goToInANewTab } from 'router/go-to';

type UserAccounts = {
    [key: string]: string;
};

export const useGetXtraRequestsData = (isAdminViewSelected: boolean) => {
    const accountRequestsDataRequested = useRef(false);
    const isLoading = useSelector(selectOfflineReportsRequestsLoading);
    const isSharedWithMeLoading = useSelector(selectOfflineReportsSharedWithMeRequestsLoading);
    const openRequests = useSelector(selectOfflineReportsOpenRequests);
    const closedRequests = useSelector(selectOfflineReportsClosedRequests);
    const sharedWithMeRequests = useSelector(selectOfflineReportsSharedWithMeRequests);
    const isAdminViewRequestsLoading = useSelector(selectOfflineReportsAccountRequestsLoading);

    const usersMap = useGetUsersMap();
    const openCatalogButtonClick = useGetOpenCatalogButtonClick();

    const allUserRequests = useMemo(() => {
        return [...openRequests, ...closedRequests];
    }, [openRequests, closedRequests]);

    const {
        getOfflineReportsRequests,
        getOfflineReportsUserAccountInfo,
        getOfflineReportsSharedWithMe,
        getOfflineReportsAccount,
    } = useOfflineReportsActions();

    const isFreemiumUser = useIsFreemiumUser();
    const { openUpgradePlanPopup } = useAppUiActions();

    useEffect(() => {
        getOfflineReportsRequests();
        getOfflineReportsUserAccountInfo();
        getOfflineReportsSharedWithMe();
    }, [
        getOfflineReportsRequests,
        getOfflineReportsSharedWithMe,
        getOfflineReportsUserAccountInfo,
    ]);

    useEffect(() => {
        if (isAdminViewSelected && !accountRequestsDataRequested.current) {
            accountRequestsDataRequested.current = true;
            getOfflineReportsAccount();
        }
    }, [getOfflineReportsAccount, isAdminViewSelected]);

    return {
        isLoading,
        isSharedWithMeLoading,
        sharedWithMeRequests,
        isFreemiumUser,
        openUpgradePlanPopup,
        usersMap,
        openCatalogButtonClick,
        allUserRequests,
        isAdminViewRequestsLoading: isAdminViewSelected && isAdminViewRequestsLoading,
    };
};

export const useGetUsersMap = () => {
    const userAccounts = useSelector(selectOfflineReportsUserAccountInfo);

    return useMemo(() => {
        const result: UserAccounts = {};

        userAccounts?.members.forEach((member) => {
            result[member.id] = member.username;
        });
        return result;
    }, [userAccounts]);
};

export const useGetOpenCatalogButtonClick = () => {
    const isFreemiumUser = useIsFreemiumUser();
    const { openUpgradePlanPopup } = useAppUiActions();

    return useCallback(() => {
        if (isFreemiumUser) {
            openUpgradePlanPopup();
        } else {
            goToInANewTab('', OPEN_CATALOG_BUTTON_PATH.slice(0, -1));
        }
    }, [isFreemiumUser, openUpgradePlanPopup]);
};
